import { useState, useEffect, useMemo, lazy, Suspense } from "react";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { C, THEMES, S } from "./config/theme";
import { useWindowWidth } from "./hooks/useWindowWidth";
import { OrgSettingsProvider, useOrgSettings } from "./hooks/OrgSettingsContext";
import { PlanProvider }        from "./hooks/PlanContext";
import { ToastProvider } from "./components/Toast";
import { AuthProvider, useAuth } from "./hooks/AuthContext";
import { useBrandedTheme } from "./hooks/useBrandedTheme";
import { usePresence }    from "./hooks/usePresence";
import { ConfirmModal, LoadingOverlay } from "./components/Modals";
import { BugReportButton } from "./components/BugReportButton";
import { FilterBar } from "./components/FilterBar";
import { generateOnePager } from "./utils/pdfExport";
import { exportFilteredLeads, exportEventSummary, exportRepPerformance } from "./utils/csvExport";
import { supabase } from "./config/supabase";
import { MfaChallengeScreen } from "./components/MfaChallengeScreen";
import { CookieBanner }      from "./components/CookieBanner";
import { EXPECTED_FIELDS, FIELD_TO_COL } from "./config/constants";
import { normalizeRanking, parseSubmissionDate, getDateRange } from "./utils/utils";
import { getLimits, isAtLimit, isNearLimit, getUpgradeTier } from "./config/planLimits";
import { WelcomeScreen } from "./components/WelcomeScreen";
import { UpgradeModal, LimitWarningBar } from "./components/UpgradeModal";
import { scoreEvents } from "./utils/scoringEngine";
// ── ChunkLoadError boundary — catches stale chunk references after new deploys ──
// When Vercel deploys a new version, old chunk filenames become 404s.
// This catches the error and reloads the page automatically.
if (typeof window !== "undefined") {
  window.addEventListener("error", (event) => {
    if (event?.message?.includes("Loading chunk") || event?.message?.includes("ChunkLoadError")) {
      console.warn("ChunkLoadError detected — reloading for fresh bundle");
      window.location.reload();
    }
  });
  window.addEventListener("unhandledrejection", (event) => {
    if (event?.reason?.message?.includes("Loading chunk") || event?.reason?.name === "ChunkLoadError") {
      console.warn("ChunkLoadError (promise) detected — reloading");
      window.location.reload();
    }
  });
}

// ── Page loading fallback ────────────────────────────────────────────────
function PageLoader() {
  return (
    <div style={{ flex: 1, display: "flex", alignItems: "center",
      justifyContent: "center", height: "100%", minHeight: 300 }}>
      <div style={{ width: 32, height: 32, border: "3px solid #22D3EE33",
        borderTop: "3px solid #22D3EE", borderRadius: "50%",
        animation: "spin 0.8s linear infinite" }} />
      <style>{"@keyframes spin { to { transform: rotate(360deg); } }"}</style>
    </div>
  );
}

// ── Page components — lazy loaded for faster initial bundle ──────────────
// Each page is only downloaded when the user first navigates to it.
const DashboardPage = lazy(() => import("./pages/DashboardPage").then(m => ({ default: m.DashboardPage })));
const EventsPage = lazy(() => import("./pages/EventsPage").then(m => ({ default: m.EventsPage })));
const GoalsPage = lazy(() => import("./pages/GoalsPage").then(m => ({ default: m.GoalsPage })));
const BudgetPage = lazy(() => import("./pages/BudgetPage").then(m => ({ default: m.BudgetPage })));
const AnnualPlanPage = lazy(() => import("./pages/AnnualPlanPage").then(m => ({ default: m.AnnualPlanPage })));
const BudgetGoalsPage = lazy(() => import("./pages/BudgetGoalsPage").then(m => ({ default: m.BudgetGoalsPage })));
const CalendarPage = lazy(() => import("./pages/CalendarPage").then(m => ({ default: m.CalendarPage })));
const BriefsPage = lazy(() => import("./pages/BriefsPage").then(m => ({ default: m.BriefsPage })));
const IntelligencePage = lazy(() => import("./pages/IntelligencePage").then(m => ({ default: m.IntelligencePage })));
const WarRoomPage = lazy(() => import("./pages/WarRoomPage").then(m => ({ default: m.WarRoomPage })));
const TeamsPage = lazy(() => import("./pages/TeamsPage").then(m => ({ default: m.TeamsPage })));
const MeetingPlannerPage = lazy(() => import("./pages/MeetingPlannerPage").then(m => ({ default: m.MeetingPlannerPage })));
const StaffSchedulingPage = lazy(() => import("./pages/StaffSchedulingPage").then(m => ({ default: m.StaffSchedulingPage })));
const ManageRosterPage = lazy(() => import("./pages/ManageRosterPage").then(m => ({ default: m.ManageRosterPage })));
const MeetingAnalyticsPage = lazy(() => import("./pages/MeetingAnalyticsPage").then(m => ({ default: m.MeetingAnalyticsPage })));
const CompetitorDashboard = lazy(() => import("./pages/CompetitorDashboard").then(m => ({ default: m.CompetitorDashboard })));
const RepsPage = lazy(() => import("./pages/RepsPage").then(m => ({ default: m.RepsPage })));
const LeadsPage = lazy(() => import("./pages/LeadsPage").then(m => ({ default: m.LeadsPage })));
const UploadPage = lazy(() => import("./pages/UploadPage").then(m => ({ default: m.UploadPage })));
const LoginPage         = lazy(() => import("./pages/LoginPage").then(m => ({ default: m.LoginPage })));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage").then(m => ({ default: m.ResetPasswordPage })));
const OnboardingPage = lazy(() => import("./pages/OnboardingPage").then(m => ({ default: m.OnboardingPage })));
const InvitePage = lazy(() => import("./pages/InvitePage").then(m => ({ default: m.InvitePage })));
const SettingsPage = lazy(() => import("./pages/SettingsPage").then(m => ({ default: m.SettingsPage })));
const LandingPage = lazy(() => import("./pages/LandingPage").then(m => ({ default: m.LandingPage })));
const PricingPage  = lazy(() => import("./pages/PricingPage").then(m => ({ default: m.PricingPage })));
const SharedBriefPage  = lazy(() => import("./pages/SharedBriefPage").then(m => ({ default: m.SharedBriefPage })));
const MeetingBookPage  = lazy(() => import("./pages/MeetingBookPage").then(m => ({ default: m.MeetingBookPage })));
const ApiDocsPage      = lazy(() => import("./pages/ApiDocsPage").then(m => ({ default: m.ApiDocsPage })));
const ContactPage      = lazy(() => import("./pages/ContactPage").then(m => ({ default: m.ContactPage })));
const HelpPage         = lazy(() => import("./pages/HelpPage").then(m => ({ default: m.HelpPage })));
const MobileAppPage    = lazy(() => import("./pages/MobileAppPage").then(m => ({ default: m.MobileAppPage })));
const AboutPage        = lazy(() => import("./pages/AboutPage").then(m => ({ default: m.AboutPage })));
const BlogPage         = lazy(() => import("./pages/BlogPage").then(m => ({ default: m.BlogPage })));
const InviteAcceptPage = lazy(() => import("./pages/InviteAcceptPage").then(m => ({ default: m.InviteAcceptPage })));
const EventDatabasePage = lazy(() => import("./pages/EventDatabasePage").then(m => ({ default: m.EventDatabasePage })));
const SuperAdminEventDB  = lazy(() => import("./pages/SuperAdminEventDB").then(m => ({ default: m.SuperAdminEventDB })));
const PlaybookPage = lazy(() => import("./pages/PlaybookPage").then(m => ({ default: m.PlaybookPage })));

const NAV_GROUPS = [
  { id: "analytics", label: "Analytics", tabs: [
    { id: "dashboard", label: "Lead Analytics" },
  ]},
  { id: "playbook", label: "Playbook", tabs: [
    { id: "playbook", label: "Playbook" },
  ]},
  { id: "intelligence", label: "Intelligence", tabs: [
    { id: "intelligence",  label: "Intelligence"  },
    { id: "competitors",   label: "Competitors"   },
  ]},
  { id: "events", label: "Events", tabs: [
    { id: "calendar",       label: "Calendar"       },
    { id: "budget-goals",   label: "Budget & Goals" },
    { id: "event-database", label: "Event Database" },
  ]},
  { id: "explorer", label: "Explorer", tabs: [
    { id: "events", label: "Events"        },
    { id: "teams",  label: "Account Teams" },
    { id: "reps",   label: "Reps"          },
    { id: "leads",  label: "Leads"         },
  ]},
];
const ALL_TABS = NAV_GROUPS.flatMap(g => g.tabs);
function getGroupForView(v) { return NAV_GROUPS.find(g => g.tabs.some(t => t.id === v))?.id || "playbook"; }

const DATA_TABS = ["dashboard", "events", "teams", "reps", "leads", "intelligence"];

function AppInner({ openImportOnMount = false, onImportMounted, openNewEventOnMount = false, onNewEventMounted }) {
  const w = useWindowWidth();
  const isMobile = w < 768;
  const [settingsTab, setSettingsTab] = useState("general");
  const [view, setView] = useState(() => {
    // Check if WelcomeScreen set a pending navigation after tour reload
    try {
      const pending = sessionStorage.getItem("exiq_welcome_nav");
      if (pending) { sessionStorage.removeItem("exiq_welcome_nav"); return pending; }
    } catch {}
    return "dashboard";
  });

  // Listen for background navigation from WelcomeScreen tour
  useEffect(() => {
    const handler = e => { if (e.detail) setView(e.detail); };
    window.addEventListener("exiq_nav", handler);
    return () => window.removeEventListener("exiq_nav", handler);
  }, []);

  // Listen for intelligence message seeding from WelcomeScreen tour
  useEffect(() => {
    const handler = e => { if (e.detail) setIntelMessages(e.detail); };
    window.addEventListener("exiq_intel_seed", handler);
    return () => window.removeEventListener("exiq_intel_seed", handler);
  }, []);

  const [mobileNav, setMobileNav] = useState(false);
  const [allData, setAllData] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [eventOverrides, setEventOverrides] = useState({});
  const [dataLoading, setDataLoading] = useState(true);
  const [confirmModal, setConfirmModal] = useState(null);
  const [activeGroup, setActiveGroup] = useState("analytics");

  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [orgLimits, setOrgLimits] = useState({ max_events: 999, max_users: 999, subscription_tier: "professional" });
  const [lineItemActuals, setLineItemActuals] = useState({});
  const [budgetDeepLinkEvent, setBudgetDeepLinkEvent] = useState(null);
  const [calendarEvents, setCalendarEvents] = useState([]);
  // Intelligence chat — persists across tab navigation (Option A session persistence)
  const [intelMessages,     setIntelMessages]     = useState([]);
  const [intelPendingChart, setIntelPendingChart] = useState(null);
  const [intelChartSaved,   setIntelChartSaved]   = useState(null);
  const [meetingCounts, setMeetingCounts] = useState({}); // live counts from event_meetings
  const [deepLinkEvent,  setDeepLinkEvent]  = useState(null); // event name to pre-select on nav
  const [playbookEvent, setPlaybookEvent] = useState(null); // full event object for Playbook page
  const [annualBudget, setAnnualBudget] = useState(0);

  // ─── Global Filters ───
  const [filterEvent, setFilterEvent] = useState("all");
  const [filterRep, setFilterRep] = useState("all");
  const [filterBooth, setFilterBooth] = useState("all");
  const [filterTeam, setFilterTeam] = useState("all");
  const [qualFilters, setQualFilters] = useState({});
  const [datePreset, setDatePreset] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const clearFilters = () => { setFilterEvent("all"); setFilterRep("all"); setFilterBooth("all"); setFilterTeam("all"); setDatePreset("all"); setDateFrom(""); setDateTo(""); setQualFilters({}); };

  const handleDownloadPdf = async () => {
    setPdfGenerating(true);
    try {
      await generateOnePager({ filteredData, allData, eventOverrides, orgSettings: settings, lineItemActuals, qualificationFields });
    } catch (err) {
      console.error("PDF error:", err);
    } finally {
      setPdfGenerating(false);
    }
  };

  const { T, headerBg } = useBrandedTheme();

  const primaryColor = C.primary;
  const { branding, fyStartMonth, settings, qualificationFields, hasFeature } = useOrgSettings();
  const planFeatures = settings?.plan_features || {};
  const { user, profile, loading: authLoading, signOut, orgId, isAdmin, isManager } = useAuth();

  // ── Realtime presence — after ALL hooks so no temporal dead zone ──────────
  const presencePage = activeGroup
    ? activeGroup.charAt(0).toUpperCase() + activeGroup.slice(1)
    : "Dashboard";
  const { online } = usePresence({
    orgId:       orgId,
    userId:      user?.id,
    userName:    profile?.full_name || profile?.name || "",
    userEmail:   user?.email || "",
    currentPage: presencePage,
    activeEvent: "",
  });

  const companyName = branding?.company_name || profile?.company_name || "EXIQ";
  const logoUrl = branding?.logo_url || null;

  useEffect(() => {
    if (!user || !profile) { setDataLoading(false); return; }
    if (!profile.onboarding_complete) { setDataLoading(false); return; }
    loadAll();
  }, [user?.id, profile?.org_id, profile?.onboarding_complete]);

  async function loadAll() {
    setDataLoading(true);
    try {
      // ── All queries fire in parallel — no sequential waiting ──────────────
      // Before: 5 sequential round trips (~800ms-1.5s)
      // After:  1 Promise.all (~150-300ms)
      const LEADS_COLS = [
        "id", "org_id", "source_file", "custom_data",
        "primary_campaign_source", "marketing_influenced", "submission_date",
        "sales_executive", "first_name", "last_name", "email", "phone",
        "company", "job_title", "address", "classification",
        "application_in_use", "received_demo", "ranking", "total_influenced_pipeline",
      ].join(", ");

      const queries = [
        // 0: leads — only needed columns, not select("*")
        orgId
          ? supabase.from("leads").select(LEADS_COLS).eq("org_id", orgId).order("id")
          : supabase.from("leads").select(LEADS_COLS).order("id"),
        // 1: uploaded_files
        orgId
          ? supabase.from("uploaded_files").select("id, file_name, row_count, uploaded_at").eq("org_id", orgId).order("id")
          : supabase.from("uploaded_files").select("id, file_name, row_count, uploaded_at").order("id"),
        // 2: event_overrides
        orgId
          ? supabase.from("event_overrides").select("event_name, total_cost, influenced_pipeline, net_new_pipeline, existing_influenced_pipeline, total_influenced_pipeline, meetings, segment, booth_size, event_date").eq("org_id", orgId)
          : supabase.from("event_overrides").select("event_name, total_cost, influenced_pipeline, net_new_pipeline, existing_influenced_pipeline, total_influenced_pipeline, meetings, segment, booth_size, event_date"),
        // 3: org limits (only if logged in)
        orgId
          ? supabase.from("organizations").select("max_events, max_users, subscription_tier").eq("id", orgId).single()
          : Promise.resolve({ data: null }),
        // 4: event_line_items (actuals for budget)
        orgId
          ? supabase.from("event_line_items").select("event_name, actual").eq("org_id", orgId)
          : Promise.resolve({ data: null }),
        // 5: events_calendar — no org_id filter (single-tenant, all rows shared)
        supabase.from("events_calendar").select("id, name, planned_budget, actual_cost, start_date, end_date, status, team, booth_size, location, goal_leads, goal_pipeline, goal_new_pipeline, goal_existing_pipeline, goal_priority_rate, goal_demo_rate, goal_meetings, goal_custom, notes, objective, is_sample").order("start_date"),
        // 6: org_budget_settings
        orgId
          ? supabase.from("org_budget_settings").select("annual_budget").eq("org_id", orgId).single()
          : Promise.resolve({ data: null }),
        // 7: meeting counts
        orgId
          ? supabase.from("event_meetings").select("event_name, status").eq("org_id", orgId).in("status", ["confirmed", "completed"])
          : Promise.resolve({ data: null }),
      ];

      const [
        leadsRes, filesRes, overridesRes, orgRes,
        lineItemsRes, calendarRes, budgetSettingsRes, meetingsRes,
      ] = await Promise.all(queries);

      // ── Process leads ────────────────────────────────────────────────────
      const mappedLeads = (leadsRes.data || []).map(row => {
        const r = {};
        EXPECTED_FIELDS.forEach(f => { r[f] = row[FIELD_TO_COL[f]] || ""; });
        r._source_file = row.source_file || "";
        if (row.custom_data && typeof row.custom_data === "object") {
          r.custom_data = row.custom_data;
        }
        return r;
      });

      // ── Process files ─────────────────────────────────────────────────────
      const mappedFiles = (filesRes.data || []).map(f => ({
        id: f.id, name: f.file_name, rows: f.row_count,
        date: new Date(f.uploaded_at).toLocaleDateString(), rawDate: f.uploaded_at,
      }));

      // ── Process overrides ─────────────────────────────────────────────────
      const overrideMap = {};
      (overridesRes.data || []).forEach(r => {
        overrideMap[r.event_name] = {
          total_cost: r.total_cost || 0,
          influenced_pipeline: r.influenced_pipeline || 0,
          net_new_pipeline: r.net_new_pipeline || 0,
          existing_influenced_pipeline: r.existing_influenced_pipeline || 0,
          total_influenced_pipeline: r.total_influenced_pipeline || 0,
          meetings: r.meetings || 0, segment: r.segment || "",
          booth_size: r.booth_size || "", event_date: r.event_date || "",
        };
      });

      // ── Org limits ────────────────────────────────────────────────────────
      if (orgRes.data) {
        const tier       = orgRes.data.subscription_tier || "professional";
        const tierLimits = getLimits(tier);
        setOrgLimits({
          max_events:        tierLimits.events ?? 999,
          max_users:         tierLimits.users  ?? 999,
          subscription_tier: tier,
        });
      }

      // ── Budget / line items ───────────────────────────────────────────────
      if (lineItemsRes.data) {
        const actuals = {};
        lineItemsRes.data.forEach(item => {
          if (!actuals[item.event_name]) actuals[item.event_name] = 0;
          actuals[item.event_name] += parseFloat(item.actual) || 0;
        });
        setLineItemActuals(actuals);
      }
      if (calendarRes.data) {
        setCalendarEvents(calendarRes.data);
        // Enrich overrideMap with data from events_calendar so Playbook
        // is the single source of truth — overrides win if already set,
        // calendar values fill in when overrides are missing.
        calendarRes.data.forEach(ev => {
          if (!overrideMap[ev.name]) overrideMap[ev.name] = {};

          // actual_cost fallback
          if (ev.actual_cost > 0 && !overrideMap[ev.name].total_cost) {
            overrideMap[ev.name].total_cost = ev.actual_cost;
          }

          // segment/team — read from event_overrides.segment first,
          // fall back to events_calendar.team set in Playbook Discover
          if (!overrideMap[ev.name].segment && ev.team) {
            overrideMap[ev.name].segment = ev.team;
          }

          // booth_size — fall back to events_calendar.booth_size
          if (!overrideMap[ev.name].booth_size && ev.booth_size) {
            overrideMap[ev.name].booth_size = ev.booth_size;
          }
        });
      }
      if (budgetSettingsRes.data?.annual_budget)    setAnnualBudget(budgetSettingsRes.data.annual_budget);

      // ── Meeting counts ────────────────────────────────────────────────────
      if (meetingsRes.data) {
        const counts = {};
        meetingsRes.data.forEach(m => {
          counts[m.event_name] = (counts[m.event_name] || 0) + 1;
        });
        setMeetingCounts(counts);
      }

      setAllData(mappedLeads);
      setUploadedFiles(mappedFiles);
      setEventOverrides(overrideMap);
      if (mappedLeads.length > 0) setView("dashboard");
    } catch (err) {
      console.error("Error loading data:", err);
    } finally {
      setDataLoading(false);
    }
  }

  // ─── Compute filtered data globally ───
  const filteredData = useMemo(() => {
    let d = allData.filter(r => (r["Primary Campaign Source"] || "").trim() !== "");
    if (datePreset !== "all") {
      const [from, to] = getDateRange(datePreset, dateFrom, dateTo, fyStartMonth);
      if (from || to) d = d.filter(r => { const rd = parseSubmissionDate(r["Submission Date"]); if (!rd) return false; if (from && rd < from) return false; if (to && rd > to) return false; return true; });
    }
    if (filterEvent !== "all") d = d.filter(r => r["Primary Campaign Source"] === filterEvent);
    if (filterRep !== "all") d = d.filter(r => (r["Sales Executive"] || "").trim() === filterRep);
    if (filterBooth !== "all") d = d.filter(r => (eventOverrides[r["Primary Campaign Source"]] || {}).booth_size === filterBooth);
    Object.entries(qualFilters).forEach(([fieldId, val]) => {
      if (!val || val === "all") return;
      d = d.filter(r => {
        const cd = r.custom_data || {};
        const raw = cd[fieldId];
        if (raw === undefined || raw === null || raw === "") return false;
        const normalized = String(raw).trim().toLowerCase();
        const valNorm = String(val).trim().toLowerCase();
        if (valNorm === "yes") return ["yes","y","true","1"].includes(normalized);
        if (valNorm === "no") return !["yes","y","true","1"].includes(normalized);
        // Match the stored value against the filter value
        // Also match if the filter value appears anywhere in the stored string
        // This handles cases where data is stored as label ("Priority") but filter sends value ("hot")
        const qf = (qualificationFields || []).find(f => f.id === fieldId);
        if (qf && qf.options) {
          const targetOpt = qf.options.find(o => o.value === valNorm);
          if (targetOpt) {
            const labelNorm = (targetOpt.label || "").toLowerCase();
            const shortNorm = (targetOpt.short_label || "").toLowerCase();
            return normalized === valNorm || normalized === labelNorm || normalized === shortNorm;
          }
        }
        return normalized === valNorm;
      });
    });
    if (filterTeam !== "all") d = d.filter(r => (eventOverrides[r["Primary Campaign Source"]] || {}).segment === filterTeam);
    return d;
  }, [allData, filterEvent, filterRep, filterBooth, filterTeam, qualFilters, qualificationFields, eventOverrides, datePreset, dateFrom, dateTo, fyStartMonth]);


  const handleOnboardingComplete = () => {
    // Clear any stale tour state so it never bleeds into fresh onboarding
    try { localStorage.removeItem("exiq_welcome_dismissed"); } catch {}
    try { sessionStorage.removeItem("exiq_tour_active"); } catch {}
    try { sessionStorage.removeItem("exiq_tour_step"); } catch {}
    try { sessionStorage.removeItem("exiq_welcome_nav"); } catch {}
    window.location.reload();
  };

  // Auth loading and login handled by AppWithOrg

  if (user && profile && !profile.onboarding_complete) return <Suspense fallback={<PageLoader />}><OnboardingPage width={w} theme={T} onComplete={handleOnboardingComplete} /></Suspense>;

  const eventCount    = calendarEvents.length; // count calendar events, not uploaded lead sources
  const maxEvents     = orgLimits.max_events ?? 999;
  const maxUsers      = orgLimits.max_users  ?? 999;
  const currentTier   = orgLimits.subscription_tier || "professional";
  const eventLimitPct = maxEvents < 999 ? eventCount / maxEvents : 0;
  const [upgradeModal, setUpgradeModal] = useState(null); // { reason, currentCount, limit }

  // Helper: show upgrade modal once per session per reason
  function showUpgrade(reason, currentCount = 0, limit = 0) {
    try {
      if (sessionStorage.getItem(`upgrade_dismissed_${reason}`)) return;
    } catch {}
    setUpgradeModal({ reason, currentCount, limit });
  }

  // Reload event_overrides from Supabase into state (called after CRM pull)
  async function reloadEventOverrides() {
    if (!orgId) return;
    try {
      const { data } = await supabase.from("event_overrides").select("*").eq("org_id", orgId);
      if (data) {
        const map = {};
        data.forEach(r => { map[r.event_name] = r; });
        setEventOverrides(map);
      }
    } catch (e) {
      console.error("Failed to reload event overrides:", e);
    }
  }

  const tourActive = (() => { try { return sessionStorage.getItem("exiq_tour_active") === "1"; } catch { return false; } })();

  const pageProps = {
    theme: T,
    allData, filteredData, setAllData, uploadedFiles, setUploadedFiles,
    eventOverrides, setEventOverrides, reloadEventOverrides, dataLoading, setDataLoading,
    setView, setDeepLinkEvent, setConfirmModal, width: w, orgId, isAdmin, isManager, setPlaybookEvent, setSettingsTab,
    eventCount, maxEvents, orgLimits,
    lineItemActuals, setLineItemActuals, calendarEvents, setCalendarEvents, annualBudget, setAnnualBudget, meetingCounts,
    onDownloadPdf: handleDownloadPdf, pdfGenerating,
    tourActive,
  };
  const hasData = allData.length > 0;
  useEffect(() => { if (allData.length > 0 && view === "upload") setView("dashboard"); }, [allData.length]);

  // Auto-select tour event (SOF Week) when tour dispatches exiq_select_event
  useEffect(() => {
    const handler = e => {
      if (!e.detail) return;
      const ev = calendarEvents.find(ce => ce.name === e.detail);
      if (ev) setPlaybookEvent(ev);
    };
    window.addEventListener("exiq_select_event", handler);
    return () => window.removeEventListener("exiq_select_event", handler);
  }, [calendarEvents]);
  const latestUpload = uploadedFiles.length > 0 ? uploadedFiles.reduce((m, f) => f.rawDate > m ? f.rawDate : m, "") : null;

  const showFilters = DATA_TABS.includes(view) && hasData && !view.startsWith("intelligence");

  const renderPage = () => {
    switch (view) {
      case "dashboard": return <DashboardPage {...pageProps} />;
      case "events":    return <EventsPage {...pageProps} />;
      case "teams":     return <TeamsPage {...pageProps} />;
      case "budget-goals":
      case "goals":
      case "budget":
      case "annual-plan":
        return <BudgetGoalsPage theme={T} width={w} orgId={orgId} isManager={isManager} isAdmin={isAdmin} eventOverrides={eventOverrides} lineItemActuals={lineItemActuals} setLineItemActuals={setLineItemActuals} calendarEvents={calendarEvents} annualBudget={annualBudget} budgetDeepLinkEvent={budgetDeepLinkEvent} setBudgetDeepLinkEvent={setBudgetDeepLinkEvent} allData={allData} />;
      case "intelligence":
        return <IntelligencePage theme={T} width={w} filteredData={filteredData} allData={allData} eventOverrides={eventOverrides} orgSettings={settings} orgId={orgId} setView={setView} setSettingsTab={setSettingsTab} calendarEvents={calendarEvents} messages={intelMessages} setMessages={setIntelMessages} pendingChartProp={intelPendingChart} setPendingChartProp={setIntelPendingChart} chartSavedProp={intelChartSaved} setChartSavedProp={setIntelChartSaved} />;
      case "competitors":
        return <CompetitorDashboard theme={T} width={w} orgId={orgId} calendarEvents={calendarEvents} setView={setView} setSettingsTab={setSettingsTab} />;
      case "meetings":  return <MeetingPlannerPage theme={T} width={w} orgId={orgId} isManager={isManager} isAdmin={isAdmin} calendarEvents={calendarEvents} initialEvent={deepLinkEvent} onMounted={() => setDeepLinkEvent(null)} />;
      case "staff":     return <StaffSchedulingPage theme={T} width={w} orgId={orgId} isManager={isManager} isAdmin={isAdmin} calendarEvents={calendarEvents} onNavigate={setView} initialEvent={deepLinkEvent} onMounted={() => setDeepLinkEvent(null)} />;
      case "roster":    return <ManageRosterPage theme={T} width={w} orgId={orgId} isAdmin={isAdmin} onNavigate={setView} />;
      case "meeting-analytics": return <MeetingAnalyticsPage theme={T} width={w} orgId={orgId} calendarEvents={calendarEvents} eventOverrides={eventOverrides} />;
      case "playbook": return <PlaybookPage theme={T} width={w} event={playbookEvent} setEvent={setPlaybookEvent} orgId={orgId} orgSettings={settings} eventOverrides={eventOverrides} allData={allData} calendarEvents={calendarEvents} setCalendarEvents={setCalendarEvents} setLineItemActuals={setLineItemActuals} onBack={() => setView("calendar")} setView={setView} setDeepLinkEvent={setDeepLinkEvent} eventCount={eventCount} maxEvents={maxEvents} currentTier={currentTier} onEventLimitHit={() => showUpgrade("event_limit", eventCount, maxEvents)} openImportOnMount={openImportOnMount} onImportMounted={onImportMounted} openNewEventOnMount={openNewEventOnMount} onNewEventMounted={onNewEventMounted} reloadEventOverrides={reloadEventOverrides} />;
      case "planner":
      case "calendar": return <CalendarPage theme={T} width={w} orgId={orgId} isManager={isManager} lineItemActuals={lineItemActuals} setLineItemActuals={setLineItemActuals} setCalendarEvents={setCalendarEvents} setView={setView} setBudgetDeepLinkEvent={setBudgetDeepLinkEvent} planFeatures={planFeatures} setDeepLinkEvent={setDeepLinkEvent} deepLinkEvent={deepLinkEvent} onMounted={() => setDeepLinkEvent(null)} setPlaybookEvent={setPlaybookEvent} eventCount={eventCount} maxEvents={maxEvents} currentTier={currentTier} onEventLimitHit={() => showUpgrade("event_limit", eventCount, maxEvents)} />;
      case "briefs":   return <BriefsPage theme={T} width={w} orgId={orgId} calendarEvents={calendarEvents} allData={allData} eventOverrides={eventOverrides} isManager={isManager} isAdmin={isAdmin} planFeatures={planFeatures} initialEvent={deepLinkEvent} onMounted={() => setDeepLinkEvent(null)} />;
      case "reps":      return <RepsPage {...pageProps} />;
      case "warroom":
        return <WarRoomPage theme={T} orgId={orgId} calendarEvents={calendarEvents} />;
      case "event-database":
        return <EventDatabasePage theme={T} width={w} orgId={orgId} calendarEvents={calendarEvents} setCalendarEvents={setCalendarEvents} setView={setView} setPlaybookEvent={setPlaybookEvent} />;
      case "superadmin-events":
        return <SuperAdminEventDB theme={T} width={w} orgId={orgId} />;
      case "leads":     return <LeadsPage {...pageProps} />;
      case "upload":    return <UploadPage {...pageProps} />;
      case "settings":  return <SettingsPage theme={T} width={w} orgId={orgId} eventCount={eventCount} maxEvents={maxEvents} currentTier={currentTier} maxUsers={maxUsers} onUserLimitHit={() => showUpgrade("user_limit", 0, maxUsers)} initialTab={settingsTab} />;
      case "invite":    return <InvitePage theme={T} width={w} orgId={orgId} />;
      default:          return <DashboardPage {...pageProps} />;
    }
  };

  return (
    <div style={{ ...S.app, background: T.bg, color: T.textBold, overflowX: "hidden" }}>
      {/* Skip to main content — accessibility */}
      <a href="#main-content" style={{
        position: "absolute", top: -100, left: 8, zIndex: 9999,
        background: "#22D3EE", color: "#0A1720", fontWeight: 700,
        padding: "8px 16px", borderRadius: 4, fontSize: 13,
        transition: "top 0.1s",
      }}
      onFocus={e => { e.currentTarget.style.top = "8px"; }}
      onBlur={e  => { e.currentTarget.style.top = "-100px"; }}>
        Skip to main content
      </a>
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;900&display=swap" rel="stylesheet" />
      <style>{`
        table tbody tr { transition: background 0.12s; }
        table tbody tr:hover { background: ${T.hoverBg} !important; }
        * { box-sizing: border-box; }
        :root { --primary: ${primaryColor}; }
        input[type=range] { -webkit-appearance: none; appearance: none; height: 4px; border-radius: 2px; background: transparent; outline: none; cursor: pointer; }
        input[type=range]::-webkit-slider-thumb { -webkit-appearance: none; appearance: none; width: 14px; height: 14px; border-radius: 50%; cursor: pointer; }
        input:focus-visible, select:focus-visible, textarea:focus-visible, button:focus-visible { outline: none; border-color: #22D3EE !important; box-shadow: 0 0 0 3px #22D3EE33 !important; }

        /* ── EXIQ Brand Input Styles ─────────────────────────────── */
        /* Targets all inputs inside playbook panels and settings forms */

        /* Base input / select / textarea */
        input:not([type=range]):not([type=checkbox]):not([type=radio]):not([type=file]):not([type=color]),
        select,
        textarea {
          background: #142D3E !important;
          color: #FFFFFF !important;
          border: 1px solid #142D3E !important;
          border-radius: 6px !important;
          padding: 8px 10px !important;
          font-size: 13px !important;
          font-family: 'Inter', sans-serif !important;
          transition: border-color 0.15s, box-shadow 0.15s !important;
        }

        /* Placeholder text — textFaint */
        input::placeholder, textarea::placeholder { color: #535C62 !important; opacity: 1 !important; }

        /* Focus state — cyan ring */
        input:not([type=range]):not([type=checkbox]):not([type=radio]):focus,
        select:focus,
        textarea:focus {
          border-color: #22D3EE44 !important;
          box-shadow: 0 0 0 2px #22D3EE11 !important;
          outline: none !important;
        }

        /* Dropdown arrow — keep consistent */
        select {
          appearance: none !important;
          -webkit-appearance: none !important;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath d='M2 4l4 4 4-4' stroke='%237A8894' stroke-width='1.5' fill='none' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E") !important;
          background-repeat: no-repeat !important;
          background-position: right 10px center !important;
          padding-right: 30px !important;
        }

        /* Textarea — allow vertical resize only */
        textarea { resize: vertical !important; line-height: 1.5 !important; }

        /* Date / time inputs — keep styled */
        input[type=date], input[type=time] {
          background: #142D3E !important;
          color: #FFFFFF !important;
          border: 1px solid #142D3E !important;
          border-radius: 6px !important;
          padding: 7px 8px !important;
          font-size: 12px !important;
          font-family: 'Inter', sans-serif !important;
          color-scheme: dark !important;
        }

        /* Primary button style */
        button[data-primary] {
          background: #22D3EE !important;
          color: #0A1720 !important;
          border: none !important;
          border-radius: 6px !important;
          padding: 9px 22px !important;
          font-size: 13px !important;
          font-weight: 700 !important;
          cursor: pointer !important;
          font-family: 'Inter', sans-serif !important;
        }

        /* Select option bg */
        select option { background: #142D3E; color: #FFFFFF; }

        /* Search inputs — keep override */
        input[type=search] { background: #142D3E !important; color: #FFFFFF !important; }
      `}</style>

      {/* ═══ HEADER ═══ */}
      <div role="navigation" aria-label="Application navigation" style={{ ...S.header(w), background: headerBg, borderBottom: `3px solid ${primaryColor}` }}>
        <div style={{ maxWidth: 1480, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", minHeight: 48, padding: w < 640 ? "6px 0" : "8px 0", borderBottom: `1px solid ${T.border}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ width: 32, height: 32, background: primaryColor, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, overflow: "hidden", flexShrink: 0 }}>
              {logoUrl ? <img src={logoUrl} alt="logo" style={{ width: "100%", height: "100%", objectFit: "contain" }} /> : ""}
            </div>
            {w >= 480 && (<><div style={{ width: 1, height: 28, background: `${C.stone}44`, margin: "0 4px" }} /><div><div style={{ fontSize: w < 640 ? 11 : 13, fontWeight: 700, color: "#E6E7E8", letterSpacing: "0.04em" }}>{companyName}</div><div style={{ fontSize: 9, color: "#B5B9BC", letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 700 }}>Exhibitor Intelligence</div></div></>)}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {/* ── Presence avatars ── */}
            {online.length > 0 && w >= 640 && (() => {
              const MAX = 4;
              const shown  = online.slice(0, MAX);
              const hidden = online.length - MAX;
              return (
                <div style={{ display: "flex", alignItems: "center", gap: 2, marginRight: 4 }}>
                  {shown.map((u, i) => (
                    <div key={u.userId} title={`${u.name}${u.page ? " · " + u.page : ""}${u.activeEvent ? " / " + u.activeEvent : ""}`}
                      style={{ width: 28, height: 28, borderRadius: "50%",
                        background: u.color, border: "2px solid #0A1720",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 10, fontWeight: 700, color: "#FFFFFF",
                        fontFamily: "'Inter',sans-serif",
                        marginLeft: i > 0 ? -6 : 0, zIndex: MAX - i,
                        position: "relative", cursor: "default",
                        boxShadow: u.activeEvent ? `0 0 0 2px ${u.color}` : "none",
                      }}>
                      {u.initials}
                    </div>
                  ))}
                  {hidden > 0 && (
                    <div style={{ width: 28, height: 28, borderRadius: "50%",
                      background: "#1C3A50", border: "2px solid #0A1720",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 9, fontWeight: 700, color: "#B5B9BC",
                      fontFamily: "'Inter',sans-serif", marginLeft: -6,
                      position: "relative", zIndex: 0 }}>
                      +{hidden}
                    </div>
                  )}
                </div>
              );
            })()}
            {latestUpload && w >= 768 && <span style={{ fontSize: 10, color: "#B5B9BC", fontWeight: 700, whiteSpace: "nowrap" }}>Updated {new Date(latestUpload).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>}
            <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 4, background: isAdmin ? `${primaryColor}22` : `${C.stone}22`, color: isAdmin ? primaryColor : "#7A8894", textTransform: "uppercase", letterSpacing: "0.06em" }}>
              {isAdmin ? "Admin" : isManager ? "Manager" : "Viewer"}
            </span>
            <div style={{ position: "relative" }}>
              <button onClick={() => setShowUserMenu(m => !m)}
                style={{ background: showUserMenu ? `${primaryColor}22` : "none", border: `1px solid ${C.stone}44`, borderRadius: 5, padding: "4px 12px", cursor: "pointer", fontSize: 11, fontFamily: "'Inter',sans-serif", fontWeight: 700, color: "#9BA8B0", display: "flex", alignItems: "center", gap: 4 }} aria-label="User menu">
                {user?.email?.split("@")[0]} ▾
              </button>
              {showUserMenu && (
                <div style={{ position: "absolute", top: "calc(100% + 6px)", right: 0, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 8, padding: "6px 0", minWidth: 160, zIndex: 300, boxShadow: "0 4px 16px rgba(0,0,0,0.3)" }} onMouseLeave={() => setShowUserMenu(false)}>
                  {isManager && <button onClick={() => { setView("settings"); setShowUserMenu(false); }} style={{ display: "block", width: "100%", textAlign: "left", padding: "9px 16px", background: "none", border: "none", fontSize: 13, fontFamily: "'Inter',sans-serif", color: view === "settings" ? primaryColor : T.text, cursor: "pointer", fontWeight: view === "settings" ? 700 : 400 }} aria-label="User menu">Settings</button>}
                  
                  <div style={{ height: 1, background: T.border, margin: "4px 0" }} />
                  <button onClick={() => { signOut(); setShowUserMenu(false); }} style={{ display: "block", width: "100%", textAlign: "left", padding: "9px 16px", background: "none", border: "none", fontSize: 13, fontFamily: "'Inter',sans-serif", color: T.textSec, cursor: "pointer" }} aria-label="User menu">Sign Out</button>
                </div>
              )}
            </div>
            {isMobile && <button onClick={() => setMobileNav(n => !n)} style={{ background: "none", border: `1px solid ${T.border}`, borderRadius: 6, padding: "6px 10px", cursor: "pointer", color: T.textSec, fontSize: 18, lineHeight: 1 }} aria-label="Toggle navigation menu" aria-expanded={mobileNav}>{mobileNav ? "✕" : "☰"}</button>}
          </div>
        </div>

        {!isMobile && (
          <div style={{ maxWidth: 1480, margin: "0 auto" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 0", borderBottom: `1px solid ${T.border}33` }}>
              <div style={{ display: "flex", gap: 4 }}>
                {NAV_GROUPS.map(group => {
                  const isActive = activeGroup === group.id;
                  return (
                    <button key={group.id} onClick={() => {
                        setActiveGroup(group.id);
                        if (group.id === "intelligence") { setView("intelligence"); }
                        else if (group.tabs[0]) setView(group.tabs[0].id);
                        window.scrollTo({ top: 0, behavior: "instant" });
                      }}
                      style={{ padding: "6px 18px", borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: "pointer", border: `1px solid ${isActive ? primaryColor + "44" : "transparent"}`, fontFamily: "'Inter',sans-serif", background: isActive ? `${primaryColor}22` : "transparent", color: isActive ? primaryColor : "#9BA8B0", transition: "all 0.15s" }}>
                      {group.label}
                    </button>
                  );
                })}
              </div>
              <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                <div style={{ width: 1, height: 20, background: `${C.stone}33`, margin: "0 2px" }} />



              </div>
            </div>
            {/* Sub-tab row — Intelligence manages its own tabs internally, all others show here */}
            {(() => {
              const group = NAV_GROUPS.find(g => g.id === activeGroup);
              if (!group || group.id === "intelligence") return <div style={{ height: 38 }} />;
              if (group.tabs.length === 0) return <div style={{ height: 38 }} />;
              return (
                <div style={{ display: "flex", gap: 2, padding: "4px 0" }}>
                  {group.tabs.map(tab => {
                    const isActive = view === tab.id;
                    return (
                      <button key={tab.id} onClick={() => { setView(tab.id); window.scrollTo({ top: 0, behavior: "instant" }); }}
                        style={{ padding: "7px 16px", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: "pointer", border: "none", fontFamily: "'Inter',sans-serif", background: isActive ? primaryColor : "transparent", color: isActive ? "#FFFFFF" : "#9BA8B0", transition: "all 0.15s", borderBottom: isActive ? `2px solid ${primaryColor}` : "2px solid transparent" }}>
                        {tab.label}
                      </button>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        )}

        {isMobile && mobileNav && (
          <div style={{ display: "flex", flexDirection: "column", gap: 4, padding: "0 8px 12px" }}>
            {[...ALL_TABS, ...(isManager ? [{ id: "upload", label: "＋ Upload" }] : [])].map(tab => (
              <button key={tab.id} style={{ padding: "10px 14px", borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: "pointer", border: "none", fontFamily: "'Inter',sans-serif", background: view === tab.id ? primaryColor : "transparent", color: view === tab.id ? "#FFFFFF" : T.textSec, textAlign: "left" }} onClick={() => { setView(tab.id); setMobileNav(false); }} aria-label="Toggle navigation menu" aria-expanded={mobileNav}>{tab.label}</button>
            ))}
          </div>
        )}
      </div>

      <div id="main-content" role="main" style={{ ...S.main(w), position: "relative", zIndex: 1 }}>
        {showFilters && (
          <FilterBar primaryColor={primaryColor} allData={allData} eventOverrides={eventOverrides}
            filterEvent={filterEvent} setFilterEvent={setFilterEvent}
            filterRep={filterRep} setFilterRep={setFilterRep}
            filterBooth={filterBooth} setFilterBooth={setFilterBooth}
            filterTeam={filterTeam} setFilterTeam={setFilterTeam}
            qualFilters={qualFilters} setQualFilters={setQualFilters}
            datePreset={datePreset} setDatePreset={setDatePreset}
            dateFrom={dateFrom} setDateFrom={setDateFrom}
            dateTo={dateTo} setDateTo={setDateTo}
            showMoreFilters={showMoreFilters} setShowMoreFilters={setShowMoreFilters}
            onClear={clearFilters} theme={T} filteredCount={filteredData.length} />
        )}
        {eventLimitPct >= 0.8 && currentTier === "starter" && DATA_TABS.includes(view) && view !== "upload" && (
          <LimitWarningBar
            resource="events"
            currentCount={eventCount}
            limit={maxEvents}
            currentTier={currentTier}
            onUpgradeClick={() => showUpgrade("event_limit", eventCount, maxEvents)}
          />
        )}
        {/* ── Upgrade modal ── */}
        {upgradeModal && (
          <UpgradeModal
            reason={upgradeModal.reason}
            currentTier={currentTier}
            currentCount={upgradeModal.currentCount}
            limit={upgradeModal.limit}
            onClose={() => setUpgradeModal(null)}
          />
        )}

        <ErrorBoundary page={view} minimal>
          <Suspense fallback={<PageLoader />}>
            {renderPage()}
          </Suspense>
        </ErrorBoundary>
      </div>
      {/* Footer */}
      {DATA_TABS.includes(view) && (
        <div style={{ textAlign: "center", padding: "20px 20px 8px", userSelect: "none" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#ffffff66", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 6 }}>
            &copy; {new Date().getFullYear()} EXIQ &middot; Confidential &middot; All Rights Reserved
          </div>
          <div style={{ fontSize: 11 }}>
            <a href="/terms" target="_blank" rel="noopener noreferrer"
              style={{ color: "#22D3EE88", textDecoration: "none", marginRight: 14 }}>Terms</a>
            <a href="/privacy" target="_blank" rel="noopener noreferrer"
              style={{ color: "#22D3EE88", textDecoration: "none" }}>Privacy</a>
          </div>
        </div>
      )}

      <BugReportButton currentPage={view} theme={T} />
      <LoadingOverlay visible={dataLoading} message="Loading..." flame={() => <div style={{ fontSize: 32 }}></div>} />
      <ConfirmModal modal={confirmModal} theme={T} />
    </div>
  );
}

function AppWithOrg() {
  const w = useWindowWidth();
  const { user, profile, orgId, loading, mfaVerified, isPasswordRecovery, reloadProfile } = useAuth();
  const [showLogin, setShowLogin] = useState(false);
  const [checkoutRedirecting, setCheckoutRedirecting] = useState(false);
  const [showWelcome, setShowWelcome]   = useState(false);
  const [importEventsOnOpen,   setImportEventsOnOpen]   = useState(false);
  const [newEventOnOpen,       setNewEventOnOpen]       = useState(false);

  // ── Show welcome screen once after onboarding ────────────────────────────
  useEffect(() => {
    if (!profile || !orgId) return;
    if (!profile.onboarding_complete) return;
    // Always show if tour is in progress (resumed after reload)
    try {
      if (sessionStorage.getItem("exiq_tour_active") === "1") {
        setShowWelcome(true);
        return;
      }
    } catch {}
    if (profile.has_seen_welcome) return;
    try { if (localStorage.getItem("exiq_welcome_dismissed")) return; } catch {}
    setShowWelcome(true);
  }, [profile?.id, profile?.has_seen_welcome, profile?.onboarding_complete, orgId]);

  // ── Pending tier checkout: fires once after signup+onboarding when orgId is ready ──
  useEffect(() => {
    if (!orgId || !user) return;
    const pendingTier = localStorage.getItem("exiq_pending_tier");
    if (!pendingTier) return;
    localStorage.removeItem("exiq_pending_tier");

    async function triggerPendingCheckout() {
      setCheckoutRedirecting(true);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) return;
        const res = await fetch("/api/create-checkout-session", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            tier: pendingTier,
            orgId,
            successUrl: `${window.location.origin}/app?checkout=success&tier=${pendingTier}`,
            cancelUrl: `${window.location.origin}/pricing`,
          }),
        });
        const data = await res.json();
        if (data.url) {
          window.location.href = data.url;
        } else {
          setCheckoutRedirecting(false);
        }
      } catch (e) {
        setCheckoutRedirecting(false);
      }
    }

    triggerPendingCheckout();
  }, [orgId, user]);

  // Loading spinner while auth initializes
  // Treat mfaVerified===null as still loading (MFA check in progress)
  // ── Checkout redirect overlay ─────────────────────────────────────────────
  if (checkoutRedirecting) return (
    <div style={{ minHeight: "100vh", background: "#0A1720", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 20, fontFamily: "'Inter',sans-serif" }}>
      <div style={{ width: 48, height: 48, background: "#22D3EE", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24 }}></div>
      <div style={{ fontSize: 16, fontWeight: 700, color: "#FFFFFF" }}>Setting up your trial…</div>
      <div style={{ fontSize: 13, color: "#B5B9BC" }}>Redirecting to secure checkout</div>
    </div>
  );

  if (loading || (user && mfaVerified === null)) return (
    <div style={{ minHeight: "100vh", background: "#0A1720", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ width: 48, height: 48, background: "#22D3EE", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24 }}></div>
    </div>
  );

  // Not logged in — show landing page or login
  if (!user) {
    const path = window.location.pathname;
    if (path === "/pricing") return (
      <Suspense fallback={<PageLoader />}>
        <PricingPage width={w} onLogin={() => setShowLogin(true)} onSignup={(tier) => {
          if (tier) localStorage.setItem("exiq_pending_tier", tier);
          setShowLogin(true);
        }} />
      </Suspense>
    );
    return showLogin
      ? <Suspense fallback={<PageLoader />}><LoginPage width={w} theme={THEMES.dark} /></Suspense>
      : (<><Suspense fallback={<PageLoader />}><LandingPage width={w} onLogin={() => setShowLogin(true)} onSignup={(tier) => {
          if (tier) localStorage.setItem("exiq_pending_tier", tier);
          setShowLogin(true);
        }} /></Suspense><CookieBanner /></>);
  }

  // User exists but profile still loading
  if (!profile) return (
    <div style={{ minHeight: "100vh", background: "#0A1720", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ width: 48, height: 48, background: "#22D3EE", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24 }}></div>
    </div>
  );

  // User + profile loaded — check MFA before rendering dashboard
  // mfaVerified === false means user has 2FA enrolled but hasn't completed the challenge
  // mfaVerified === null means still checking — treated as loading above
  if (mfaVerified === false) return <MfaChallengeScreen theme={THEMES.dark} />;

  // All checks passed — render the app
  return <OrgSettingsProvider orgId={orgId}>
      <PlanProvider orgId={orgId} userEmail={user?.email} userRole={profile?.role}>
        <>
          {showWelcome && (
            <WelcomeScreen
              userName={profile?.full_name}
              orgName={profile?.org_name}
              userId={profile?.id}
              orgId={orgId}
              reloadProfile={reloadProfile}
              onDismiss={() => setShowWelcome(false)}
              setView={v => {
                window.dispatchEvent(new CustomEvent("exiq_nav", { detail: v }));
              }}
              onImportEvents={() => {
                setImportEventsOnOpen(true);
                setShowWelcome(false);
                window.dispatchEvent(new CustomEvent("exiq_nav", { detail: "playbook" }));
              }}
              onNewEvent={() => {
                setNewEventOnOpen(true);
                setShowWelcome(false);
                window.dispatchEvent(new CustomEvent("exiq_nav", { detail: "playbook" }));
              }}

            />
          )}
          <AppInner openImportOnMount={importEventsOnOpen} onImportMounted={() => setImportEventsOnOpen(false)} openNewEventOnMount={newEventOnOpen} onNewEventMounted={() => setNewEventOnOpen(false)} />
        </>
      </PlanProvider>
      </OrgSettingsProvider>;
}

export default function AppWithAuth() {
  // ── Public routes — no auth needed, checked BEFORE AuthProvider mounts ──

  if (window.location.pathname === "/api-docs") return <Suspense fallback={<PageLoader />}><ApiDocsPage /></Suspense>;
  if (window.location.pathname === "/contact")  return <Suspense fallback={<PageLoader />}><ContactPage /></Suspense>;
  if (window.location.pathname === "/help")      return <Suspense fallback={<PageLoader />}><HelpPage width={window.innerWidth} /></Suspense>;
  if (window.location.pathname === "/mobile")   return <Suspense fallback={<PageLoader />}><MobileAppPage /></Suspense>;
  if (window.location.pathname === "/about")    return <Suspense fallback={<PageLoader />}><AboutPage /></Suspense>;
  if (window.location.pathname === "/blog")     return <Suspense fallback={<PageLoader />}><BlogPage /></Suspense>;

  // Password reset — check sessionStorage (set by AuthContext on PASSWORD_RECOVERY event)
  // OR check the URL hash directly on first load before Supabase clears it.
  // Must be outside AuthProvider so auth state can NEVER interfere.
  const isRecovery = (
    sessionStorage.getItem("exiq_password_recovery") === "1" ||
    window.location.hash.includes("type=recovery") ||
    new URLSearchParams(window.location.search).get("type") === "recovery"
  );
  if (isRecovery) {
    // Set flag so it survives any re-renders
    sessionStorage.setItem("exiq_password_recovery", "1");
    return <Suspense fallback={<PageLoader />}><ResetPasswordPage /></Suspense>;
  }

  const briefToken  = new URLSearchParams(window.location.search).get("brief");
  if (briefToken) return <Suspense fallback={<PageLoader />}><SharedBriefPage token={briefToken} /></Suspense>;

  const inviteToken = new URLSearchParams(window.location.search).get("invite");
  if (inviteToken) return <Suspense fallback={<PageLoader />}><InviteAcceptPage token={inviteToken} /></Suspense>;

  const bookToken = new URLSearchParams(window.location.search).get("book");
  if (bookToken) return <Suspense fallback={<PageLoader />}><MeetingBookPage token={bookToken} /></Suspense>;

  return <ToastProvider><AuthProvider><AppWithOrg /></AuthProvider></ToastProvider>;
}
